import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Eraser, Trash2, RefreshCw, ImageOff, PenLine, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Cellule = ({ valeur, alerte }) => (
  <td className={`px-3 py-2 text-center text-sm font-bold ${
    alerte && valeur > 0 ? "text-[#E05638]" : "text-stone-800"}`}>
    {valeur}
  </td>
);

/**
 * Nettoyage des contenus de démonstration : annonces fictives, images générées,
 * et annonces de l'administration portant un nom interne.
 */
export const NettoyageDonneesSection = ({ adminId }) => {
  const [etat, setEtat] = useState(null);
  const [chargement, setChargement] = useState(false);
  const [nomAffiche, setNomAffiche] = useState("FodindinCa");

  const charger = useCallback(() => {
    setChargement(true);
    axios
      .get(`${API}/admin/donnees-demo/inventaire`, { params: { admin_id: adminId } })
      .then((r) => setEtat(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || "Inventaire indisponible."))
      .finally(() => setChargement(false));
  }, [adminId]);

  useEffect(() => { charger(); }, [charger]);

  const purger = async (categories) => {
    const cible = categories ? categories.join(", ").toUpperCase() : "toutes les rubriques";
    if (!window.confirm(`Supprimer définitivement les annonces fictives de ${cible} ?`)) return;
    setChargement(true);
    try {
      const r = await axios.post(`${API}/admin/donnees-demo/purger`, {
        admin_id: adminId, categories: categories || null,
      });
      toast.success(`${r.data.total} annonce(s) fictive(s) supprimée(s)${
        r.data.comptes_mock_supprimes ? ` · ${r.data.comptes_mock_supprimes} compte(s) de démo` : ""}.`);
      charger();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Suppression impossible.");
    } finally {
      setChargement(false);
    }
  };

  const corrigerNoms = async () => {
    setChargement(true);
    try {
      const r = await axios.post(`${API}/admin/annonces/corriger-noms`, {
        admin_id: adminId, nom_affiche: nomAffiche,
      });
      toast.success(`${r.data.total} annonce(s) renommée(s) en « ${r.data.nom_affiche} ».`);
      charger();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Correction impossible.");
    } finally {
      setChargement(false);
    }
  };

  const totalFictives = etat?.rubriques.reduce((n, r) => n + r.fictives, 0) || 0;
  const totalAdmin = etat?.rubriques.reduce((n, r) => n + r.admin_a_corriger, 0) || 0;

  return (
    <section className="mt-14" data-testid="nettoyage-donnees-section">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="font-heading text-2xl font-extrabold text-stone-900 flex items-center gap-2.5">
          <Eraser size={24} className="text-[#0A5C36]" /> Nettoyage des annonces fictives
        </h2>
        <button onClick={charger} disabled={chargement} data-testid="nettoyage-refresh"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-stone-600 hover:text-stone-900 border border-[#E5DFD5] bg-white rounded-full px-3 py-1.5 disabled:opacity-50">
          <RefreshCw size={13} className={chargement ? "animate-spin" : ""} /> Rafraîchir
        </button>
      </div>

      {etat && (
        <>
          <div className="mt-3 flex flex-wrap gap-2 text-xs" data-testid="nettoyage-comptes-test">
            <span className={`rounded-full border px-3 py-1.5 font-semibold ${
              etat.comptes_test > 0
                ? "border-[#E05638]/30 bg-[#E05638]/[0.08] text-[#E05638]"
                : "border-[#0A5C36]/20 bg-[#0A5C36]/[0.06] text-[#0A5C36]"}`}>
              {etat.comptes_test > 0
                ? `${etat.comptes_test} compte(s) de test encore en base`
                : "Aucun compte de test en base"}
            </span>
            <span className="rounded-full border border-[#E5DFD5] bg-white px-3 py-1.5 text-stone-500">
              Nettoyage automatique à chaque redémarrage du site
            </span>
          </div>

          <div className="mt-4 overflow-x-auto rounded-2xl border border-[#E5DFD5] bg-white">
            <table className="w-full min-w-[640px]" data-testid="nettoyage-tableau">
              <thead>
                <tr className="border-b border-[#EFE9DF] text-[11px] uppercase tracking-wider text-stone-400">
                  <th className="px-3 py-2 text-left">Rubrique</th>
                  <th className="px-3 py-2">Annonces</th>
                  <th className="px-3 py-2">Visibles</th>
                  <th className="px-3 py-2">Non visibles</th>
                  <th className="px-3 py-2">Fictives</th>
                  <th className="px-3 py-2">Images de démo</th>
                  <th className="px-3 py-2">Noms admin</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {etat.rubriques.map((r) => (
                  <tr key={r.categorie} className="border-b border-[#EFE9DF] last:border-0"
                    data-testid={`nettoyage-ligne-${r.categorie}`}>
                    <td className="px-3 py-2 text-sm font-semibold text-stone-900">{r.nom}</td>
                    <Cellule valeur={r.total} />
                    <Cellule valeur={r.visibles_publiquement} />
                    <td className="px-3 py-2 text-center text-xs" data-testid={`nettoyage-statuts-${r.categorie}`}>
                      {Object.entries(r.par_statut)
                        .filter(([statut]) => statut !== "active")
                        .map(([statut, n]) => `${n} ${statut}`)
                        .join(" · ") || "—"}
                    </td>
                    <Cellule valeur={r.fictives} alerte />
                    <Cellule valeur={r.images_placeholder} alerte />
                    <Cellule valeur={r.admin_a_corriger} alerte />
                    <td className="px-3 py-2 text-right">
                      {r.fictives > 0 && (
                        <button onClick={() => purger([r.categorie])} disabled={chargement}
                          data-testid={`nettoyage-purger-${r.categorie}`}
                          className="inline-flex items-center gap-1.5 rounded-full border border-red-200 bg-white px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50 disabled:opacity-50">
                          <Trash2 size={12} /> Nettoyer
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <button onClick={() => purger(null)} disabled={chargement}
              data-testid="nettoyage-purger-tout"
              className="inline-flex items-center gap-1.5 rounded-full bg-[#E05638] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#c8482d] disabled:opacity-40">
              <Trash2 size={14} /> Tout nettoyer : {totalFictives} annonce(s) fictive(s) +{" "}
              {etat.comptes_test} compte(s) de test
            </button>
            <span className="inline-flex flex-wrap items-center gap-2">
              <input value={nomAffiche} onChange={(e) => setNomAffiche(e.target.value)} maxLength={30}
                data-testid="nettoyage-nom-input" placeholder="Nom d'annonceur public"
                className="w-48 rounded-full border border-[#E5DFD5] bg-white px-4 py-2 text-sm outline-none focus:border-[#0A5C36]" />
              <button onClick={corrigerNoms} disabled={chargement || totalAdmin === 0}
                data-testid="nettoyage-corriger-noms"
                className="inline-flex items-center gap-1.5 rounded-full bg-[#0A5C36] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#084027] disabled:opacity-40">
                <PenLine size={14} /> Corriger {totalAdmin} annonce(s) admin
              </button>
            </span>
          </div>
          <p className="mt-2 text-xs text-stone-500">
            « Noms admin » = annonces déposées par l'administration qui s'affichaient au nom
            interne (Bastion, Admin). Après correction, elles portent le nom public choisi.
          </p>

          {etat.annonces_a_verifier.length > 0 && (
            <div className="mt-5 rounded-2xl border border-amber-200 bg-amber-50 p-4"
              data-testid="nettoyage-images-a-verifier">
              <h3 className="flex items-center gap-2 text-sm font-bold text-amber-800">
                <ImageOff size={15} /> {etat.annonces_a_verifier.length} annonce(s) avec une image de
                démonstration (à remplacer par une vraie photo)
              </h3>
              <div className="mt-3 space-y-1.5">
                {etat.annonces_a_verifier.map((a) => (
                  <a key={a.annonce_id} href={`/annonces/${a.annonce_id}`} target="_blank"
                    rel="noreferrer" data-testid={`nettoyage-verifier-${a.annonce_id}`}
                    className="flex flex-wrap items-center gap-2 text-xs text-stone-700 hover:text-stone-900">
                    <span className="rounded-full border border-amber-200 bg-white px-2 py-0.5 font-semibold uppercase">
                      {a.categorie}
                    </span>
                    <span className="font-mono">{a.annonce_id.slice(0, 8)}</span>
                    <span className="italic">{a.texte || "sans texte"}</span>
                    <ExternalLink size={11} />
                  </a>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
};
